/*******************************************************************************
 * Copyright     : Copyright (c)  2025 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import React, { useState, useEffect } from "react";

//@Packages
import { useTranslation } from "react-i18next";
import { 
  Table, 
  TableHeader, 
  TableContent,
  Modal, 
  Button, 
  Banner
} from "@forge/common";

//@Components
import ActionComponent from "./Action";

//@Models
import { ISoftwareProps, IContainerVersionInfo, IImages } from "../../../models/softwaredetails";

//@Styles
import "./index.scss";

const SoftwareComponent: React.FC<ISoftwareProps> = (props) => {
  const { t } = useTranslation();
  const [showVersionInfoModal, setShowVersionInfoModal] = useState(false);
  const [selectedSoftwareKey, setSelectedSoftwareKey] = useState("");

  const setSoftwareKeyForModal = (softwareKey: string) => {
    setSelectedSoftwareKey(softwareKey);
  };

  const toggleVersionInfoModal = () => {
    setShowVersionInfoModal(!showVersionInfoModal);
  };

  const transformImagesToContainerInfo = (images?: IImages): IContainerVersionInfo[] => {
    if (!images) return [];
    
    return Object.keys(images).map((key) => ({
      image: images[key]?.name || key,
      currentVersion: images[key]?.version || '-',
      availableVersion: images[key]?.availableVersion || images[key]?.version || '-'
    }));
  };

  const renderVersionInfoComponent = (
    softwareName: string, 
    softwareKey: string, 
    softwareVersion: string, 
    containerVersionInfos: IContainerVersionInfo[]
  ) => {
    if (showVersionInfoModal && selectedSoftwareKey === softwareKey) {
      return renderVersionInfoComponentUtil(softwareName, softwareVersion, containerVersionInfos);
    }
    return null;
  };

  const displayServiceTable = () => {
    if (props.softwares.length > 0) {
      return (
        <div className="software-table-container">
          <Table>
            <TableHeader data={getTableHeaders()} />
            <TableContent data={formatTableContent()} />
          </Table>
        </div>
      );
    } else {
      return (
        <div className="no-software-container">
          <Banner 
            variant="inline" 
            type="info" 
            title=""
            description={t("Unable to fetch software list")}
            canClose={false}
          />
        </div>
      );
    }
  };

  const getTableHeaders = () => [
    {
      id: "name",
      content: t("NAME"),
      className: "w-[20rem] max-w-[20rem]",
      canSort: true
    },
    {
      id: "status",
      content: t("STATUS"),
      className: "w-[12rem] max-w-[12rem]",
      canSort: true
    },
    {
      id: "version",
      content: t("VERSION"),
      className: "w-[15rem] max-w-[15rem]",
      canSort: true
    },
    {
      id: "actions",
      content: t("ACTIONS"),
      className: "w-[15rem] max-w-[15rem]",
      canSort: false
    }
  ];

  const formatTableContent = () => {
    return props.softwares.map((softwareComponent) => {
      const { softwareName, status, softwareVersion, softwareKey, images } = softwareComponent;
      const hasUpdates = checkForUpdates(images);
      const containerInfo = transformImagesToContainerInfo(images);

      const getDisplayVersion = () => {
        if (softwareKey === 'niagara' && images) {
          const imageKeys = Object.keys(images);
          if (imageKeys.length > 0) {
            const niagaraImage = images[imageKeys[0]];
            if (niagaraImage && niagaraImage.version) {
              return niagaraImage.version;
            }
          }
        }
        return softwareVersion;
      };

      const displayVersion = getDisplayVersion();
      return {
        id: softwareKey,
        cell: [
          //Name
          { content: softwareKey === 'nextnine' ? softwareName : ( softwareName ? softwareName.charAt(0).toUpperCase() + softwareName.slice(1).toLowerCase() : "-") },
          //Status
          { 
            content: (
              <div className="status-container">
                <span className={`status-dot ${getStatusDotClass(status)}`}></span>
                <span className="status-text">
                  {formatStatusText(status)}
                </span>
              </div>
            )
          },
          //Version
          { 
            content: displayVersion ? (
              <div className="version-cell">
                <button 
                  className="version-link"
                  onClick={() => {
                    setSoftwareKeyForModal(softwareKey);
                    toggleVersionInfoModal();
                  }}
                  title={t("View version details")}
                >
                  {displayVersion}
                </button>
                {/* Show info icon only when updates available */}
                {hasUpdates && (
                  <span className="update-indicator" title={t("Update available")}>
                    ℹ️
                  </span>
                )}
                {renderVersionInfoComponent(softwareName, softwareKey, softwareVersion, containerInfo || [])}
              </div>
            ) : '-'
          },
          { 
            content: (
              <div className="action-cell">
                <ActionComponent {...props} {...softwareComponent} />
              </div>
            )
          }
        ]
      };
    });
  };

  const getStatusDotClass = (status: string) => {
  if (!status) return 'status-dot-gray';
  
  switch (status.toLowerCase()) {
    case 'installed':
    case 'running':
      return 'status-dot-green';
    case 'not_installed':
      return 'status-dot-red';
    case 'available':
      return 'status-dot-red';
    case 'installing':
    case 'uninstalling':
      return 'status-dot-orange';
    case 'failed':
    case 'error':
      return 'status-dot-red';
    default:
      return 'status-dot-gray';
  }
};

const formatStatusText = (status: string) => {
  if (!status) return 'Unknown';
  const upperCaseStatus = status.toUpperCase();
  
  const statusMap: { [key: string]: string } = {
    'INSTALLED': 'Running',
    'RUNNING': 'Running',
    'AVAILABLE': 'Not Installed',
    'NOT_INSTALLED': 'Not Installed',
    'INSTALLING': 'Installing',
    'UNINSTALLING': 'Uninstalling',
    'FAILED': 'Failed',
    'ERROR': 'Error',
    'PENDING': 'Pending'
  };
  
  return statusMap[upperCaseStatus] || status;
};

const checkForUpdates = (images?: IImages): boolean => {
  if (!images) return false;
  
  return Object.keys(images).some((key) => {
    const imageInfo = images[key];
    return imageInfo?.availableVersion && 
            imageInfo?.version && 
            imageInfo.availableVersion !== imageInfo.version;
  });
};

const capitalizeFirst = (text: string): string => {
  if (!text) return "-";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

  const renderVersionInfoComponentUtil = (
    software: string, 
    version: string, 
    containerInfo: IContainerVersionInfo[]
  ) => {
    const containerTableHeaders = [
      { id: "image", content: t("IMAGE"), canSort: true },
      { id: "currentVersion", content: t("CURRENT VERSION"), canSort: true },
      { id: "availableVersion", content: t("AVAILABLE VERSION"), canSort: true }
    ];

    const containerTableContent = containerInfo.map((container, index) => ({
      id: `container-${index}`,
      cell: [
        { content: container.image },
        { content: container.currentVersion },
        { 
          content: container.currentVersion !== container.availableVersion ? (
            <strong>{container.availableVersion}</strong>
          ) : (
            container.availableVersion
          )
        }
      ]
    }));

    return showVersionInfoModal ? (
      <Modal
        onModalClose={toggleVersionInfoModal}
        title={t("Version Details")}
        className="version-details-modal"
      >
        <div className="version-info-modal-content">
          <div className="version-info-section">
            <p className="version-info-text">
              {t("Current")} {capitalizeFirst(software)} {t("Version")}: {version}
            </p>
          </div>
          
          <div className="version-info-section">
            <h4 className="installed-images-title">
              {t("Installed images")}:
            </h4>
          </div>

          {containerInfo && containerInfo.length > 0 && (
            <div className="container-info-table">
              <Table>
                <TableHeader data={containerTableHeaders} />
                <TableContent data={containerTableContent} />
              </Table>
            </div>
          )}


          <div className="modal-footer">
            <Button 
              variant="primary"
              onClick={toggleVersionInfoModal}
            >
              {t("Done")}
            </Button>
          </div>
        </div>
      </Modal>
    ) : null;
  };

  return (
    <div className="software-component">
      {displayServiceTable()}
    </div>
  );
};

export default SoftwareComponent;